'use client';

import { useCallback, useMemo, useState } from 'react';
import { Checkbox } from '@savvy-kit/components/checkbox';
import { Input } from '@savvy-kit/components/input';
import { cn } from '@savvy-kit/lib/utils';
import { Search, X } from 'lucide-react';

import type { FilterOption } from '../../types/filter-option';
import type { SelectFilterValue } from '../../types/filter-values';

export type SelectFilterProps = {
  readonly options: FilterOption[];
  readonly value: SelectFilterValue | undefined;
  readonly onChange: (value: SelectFilterValue | undefined) => void;
  readonly className?: string;
};

export function SelectFilter({ options, value, onChange, className }: SelectFilterProps) {
  const [search, setSearch] = useState('');

  const selectedValues = useMemo(() => {
    if (!value || value.operator === 'isEmpty') {
      return new Set<string | number>();
    }
    return new Set(value.values);
  }, [value]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(searchLower));
  }, [options, search]);

  const selectedOptions = useMemo(() => {
    return options.filter((opt) => selectedValues.has(opt.value));
  }, [options, selectedValues]);

  const unselectedOptions = useMemo(() => {
    return filteredOptions.filter((opt) => !selectedValues.has(opt.value));
  }, [filteredOptions, selectedValues]);

  const handleToggle = useCallback(
    (optionValue: string | number) => {
      const newSelected = new Set(selectedValues);

      if (newSelected.has(optionValue)) {
        newSelected.delete(optionValue);
      } else {
        newSelected.add(optionValue);
      }

      if (newSelected.size === 0) {
        onChange(undefined);
      } else {
        onChange({
          operator: value?.operator === 'noneOf' ? 'noneOf' : 'anyOf',
          values: Array.from(newSelected),
        });
      }
    },
    [selectedValues, value?.operator, onChange],
  );

  const handleClearAll = useCallback(() => {
    onChange(undefined);
    setSearch('');
  }, [onChange]);

  if (options.length === 0) {
    return <div className={cn('p-4 text-sm text-muted-foreground', className)}>No options available</div>;
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Search */}
      <div className="flex items-center border-b px-3 py-2">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        <Input
          id="select-filter-search"
          type="text"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 border-0 p-0 focus-visible:ring-0"
          aria-label="Search filter options"
        />
      </div>
      {/* Options List */}
      <div className="max-h-[300px] overflow-y-auto">
        {/* Selected Section */}
        {selectedOptions.length > 0 && (
          <>
            <div className="px-3 py-2 text-xs font-medium text-muted-foreground">Selected</div>
            {selectedOptions.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted"
                htmlFor={`select-option-${option.value}`}
              >
                <Checkbox
                  id={`select-option-${option.value}`}
                  checked={true}
                  onCheckedChange={() => handleToggle(option.value)}
                  aria-label={`Deselect ${option.label}`}
                />
                {(() => {
                  if (option.emoji) {
                    return (
                      <span className="text-base leading-none shrink-0" aria-hidden="true">
                        {option.emoji}
                      </span>
                    );
                  }

                  if (option.color) {
                    return (
                      <span
                        className="h-3 w-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: option.color }}
                        aria-hidden="true"
                      />
                    );
                  }

                  return null;
                })()}
                <span className="truncate">{option.label}</span>
              </label>
            ))}
            {unselectedOptions.length > 0 && <div className="border-t" />}
          </>
        )}

        {/* Unselected Options */}
        {unselectedOptions.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted"
            htmlFor={`select-option-${option.value}`}
          >
            <Checkbox
              id={`select-option-${option.value}`}
              checked={false}
              onCheckedChange={() => handleToggle(option.value)}
              aria-label={`Select ${option.label}`}
            />
            {(() => {
              if (option.emoji) {
                return (
                  <span className="text-base leading-none shrink-0" aria-hidden="true">
                    {option.emoji}
                  </span>
                );
              }

              if (option.color) {
                return (
                  <span
                    className="h-3 w-3 shrink-0 rounded-sm"
                    style={{ backgroundColor: option.color }}
                    aria-hidden="true"
                  />
                );
              }

              return null;
            })()}
            <span className="truncate">{option.label}</span>
          </label>
        ))}

        {/* No results */}
        {filteredOptions.length === 0 && search && (
          <div className="px-3 py-4 text-center text-sm text-muted-foreground">No results for "{search}"</div>
        )}
      </div>
      {/* Clear All */}
      {selectedValues.size > 0 && (
        <button
          type="button"
          onClick={handleClearAll}
          className="flex items-center gap-2 border-t px-3 py-2 text-sm text-primary hover:bg-muted"
        >
          <X className="h-4 w-4" />
          Clear all
        </button>
      )}
    </div>
  );
}
